import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgPolicy, PolicyAcknowledgment } from '@newgenus/common';
import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../../material.module';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { MatSnackBar } from '@angular/material/snack-bar';
import firebase from 'firebase/compat/app';

export interface PolicyAcknowledgmentDialogData {
    organization: Organization;
    acknowledgment: PolicyAcknowledgment;
    policy?: OrgPolicy;
}

export interface PolicyAcknowledgmentDialogResult {
    completed: boolean;
    acknowledgment: PolicyAcknowledgment;
}

@Component({
    selector: 'shared-policy-acknowledgment-dialog',
    imports: [MaterialModule, CommonModule, FormsModule],
    template: `
        <h2 mat-dialog-title class="dialog-header">
            <mat-icon class="mr-2">fact_check</mat-icon>
            Policy Acknowledgment

            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content">
            <!-- Policy Header -->
            <div class="policy-header">
                <h3 class="policy-name">{{ data.acknowledgment.policyName }}</h3>
                <div class="policy-meta">
                    <span class="version-badge">Version {{ data.acknowledgment.policyVersion }}</span>
                    <span *ngIf="data.acknowledgment.requiresReacknowledgment" class="reack-badge">
                        <mat-icon class="inline-icon">refresh</mat-icon>
                        Re-acknowledgment Required
                    </span>
                </div>
            </div>

            <!-- Loading State -->
            <div *ngIf="isLoadingPolicy" class="loading-state">
                <mat-spinner diameter="40"></mat-spinner>
                <p>Loading policy content...</p>
            </div>

            <!-- Policy Content -->
            <div *ngIf="!isLoadingPolicy" class="policy-content-wrapper">
                <div class="policy-content-header" (click)="isPolicyExpanded = !isPolicyExpanded">
                    <span>Policy Content</span>
                    <mat-icon>{{ isPolicyExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                </div>
                <div
                    class="policy-content"
                    *ngIf="isPolicyExpanded"
                    [innerHTML]="policy?.policyWriting || 'Policy content not available.'"
                ></div>
            </div>

            <!-- Acknowledgment Stepper -->
            <div class="acknowledgment-section" *ngIf="!isLoadingPolicy">
                <h4>Complete Acknowledgment</h4>

                <mat-stepper #stepper orientation="vertical" [linear]="true">
                    <!-- Step 1: Read -->
                    <mat-step [completed]="hasRead">
                        <ng-template matStepLabel>
                            <span class="step-label">I have read this policy</span>
                        </ng-template>
                        <div class="step-content">
                            <p>Please confirm that you have read the entire policy document above.</p>
                            <mat-checkbox [(ngModel)]="hasRead" [disabled]="isSaving" (change)="onStepComplete('read')">
                                I confirm I have read this policy in its entirety
                            </mat-checkbox>
                            <div class="step-actions">
                                <button
                                    mat-flat-button
                                    color="primary"
                                    [disabled]="!hasRead || isSaving"
                                    matStepperNext
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </mat-step>

                    <!-- Step 2: Understand -->
                    <mat-step [completed]="hasUnderstood">
                        <ng-template matStepLabel>
                            <span class="step-label">I understand this policy</span>
                        </ng-template>
                        <div class="step-content">
                            <p>Please confirm that you understand the contents and implications of this policy.</p>
                            <mat-checkbox
                                [(ngModel)]="hasUnderstood"
                                [disabled]="isSaving"
                                (change)="onStepComplete('understood')"
                            >
                                I confirm I understand this policy
                            </mat-checkbox>
                            <div class="step-actions">
                                <button mat-button matStepperPrevious>Back</button>
                                <button
                                    mat-flat-button
                                    color="primary"
                                    [disabled]="!hasUnderstood || isSaving"
                                    matStepperNext
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </mat-step>

                    <!-- Step 3: Agree -->
                    <mat-step [completed]="hasAgreed">
                        <ng-template matStepLabel>
                            <span class="step-label">I agree to comply</span>
                        </ng-template>
                        <div class="step-content">
                            <p>By agreeing, you confirm that you will comply with this policy.</p>
                            <mat-checkbox
                                [(ngModel)]="hasAgreed"
                                [disabled]="isSaving"
                                (change)="onStepComplete('agreed')"
                            >
                                I agree to comply with this policy
                            </mat-checkbox>
                            <div class="step-actions">
                                <button mat-button matStepperPrevious>Back</button>
                                <button
                                    mat-flat-button
                                    color="primary"
                                    [disabled]="!hasAgreed || isSaving"
                                    (click)="completeAcknowledgment()"
                                >
                                    <mat-icon>check</mat-icon>
                                    Complete Acknowledgment
                                </button>
                            </div>
                        </div>
                    </mat-step>
                </mat-stepper>
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button [mat-dialog-close]="null" [disabled]="isSaving">Cancel</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            .dialog-header {
                display: flex;
                align-items: center;
                gap: 8px;
                padding-right: 48px;
                position: relative;
            }

            .close-button {
                position: absolute;
                right: 0;
                top: 50%;
                transform: translateY(-50%);
            }

            .dialog-content {
                min-width: 600px;
                max-height: 70vh;
                overflow-y: auto;
            }

            .policy-header {
                margin-bottom: 16px;
                padding-bottom: 16px;
                border-bottom: 1px solid #e0e0e0;
            }

            .policy-name {
                margin: 0 0 8px 0;
                font-size: 20px;
            }

            .policy-meta {
                display: flex;
                gap: 12px;
                flex-wrap: wrap;
            }

            .version-badge {
                background: color-mix(in srgb, var(--entity-color-policies, #00897b) 15%, transparent);
                color: var(--entity-color-policies, #00897b);
                padding: 4px 12px;
                border-radius: 12px;
                font-size: 12px;
            }

            .reack-badge {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                background: #fff3e0;
                color: #f57c00;
                padding: 4px 12px;
                border-radius: 12px;
                font-size: 12px;
            }

            .inline-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
            }

            .loading-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px;
                gap: 16px;
            }

            .policy-content-wrapper {
                margin-top: 16px;
                margin-bottom: 16px;
            }

            .policy-content-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 12px 16px;
                background: #3d3d3d;
                border: 1px solid #444;
                border-radius: 8px;
                cursor: pointer;
                color: #e0e0e0;
                font-weight: 500;
            }

            .policy-content-header:hover {
                background: #4d4d4d;
            }

            .policy-content {
                padding: 16px;
                background: #2d2d2d;
                border: 1px solid #444;
                border-top: none;
                border-radius: 0 0 8px 8px;
                color: #e0e0e0;
                max-height: 300px;
                overflow-y: auto;
            }

            .policy-content-header + .policy-content {
                border-radius: 0 0 8px 8px;
            }

            .policy-content-wrapper:has(.policy-content) .policy-content-header {
                border-radius: 8px 8px 0 0;
            }

            .policy-content p,
            .policy-content span,
            .policy-content div {
                color: inherit;
            }

            .acknowledgment-section {
                border-top: 1px solid #e0e0e0;
                padding-top: 16px;
            }

            .acknowledgment-section h4 {
                margin: 0 0 16px 0;
                font-size: 18px;
                color: #fff;
            }

            .step-label {
                font-size: 14px;
            }

            .step-content {
                padding: 16px 0;
            }

            .step-content p {
                margin: 0 0 16px 0;
                color: #666;
            }

            .step-actions {
                margin-top: 16px;
                display: flex;
                gap: 12px;
            }

            mat-checkbox {
                display: block;
                margin: 8px 0;
            }
        `,
    ],
})
export class PolicyAcknowledgmentDialogComponent implements OnInit {
    public isLoadingPolicy = false;
    public isSaving = false;
    public policy: OrgPolicy | null = null;
    public isPolicyExpanded = false; // Collapsed by default

    public hasRead = false;
    public hasUnderstood = false;
    public hasAgreed = false;

    // Track if this is a new acknowledgment that needs to be created
    private isNewAcknowledgment = false;
    private acknowledgmentKey = '';

    constructor(
        private dialogRef: MatDialogRef<PolicyAcknowledgmentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PolicyAcknowledgmentDialogData,
        private db: AngularFirestore,
        private auth: AngularFireAuth,
        private snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        // Check if this is a new acknowledgment (no key means it hasn't been saved yet)
        this.isNewAcknowledgment = !this.data.acknowledgment.key;
        this.acknowledgmentKey = this.data.acknowledgment.key;

        // Initialize step states from existing acknowledgment
        this.hasRead = this.data.acknowledgment.hasRead || false;
        this.hasUnderstood = this.data.acknowledgment.hasUnderstood || false;
        this.hasAgreed = this.data.acknowledgment.hasAgreed || false;

        // Load policy if not provided
        if (this.data.policy) {
            this.policy = this.data.policy;
        } else {
            this.loadPolicy();
        }
    }

    private async loadPolicy(): Promise<void> {
        this.isLoadingPolicy = true;

        try {
            const policyDoc = await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policies')
                .doc(this.data.acknowledgment.policyKey)
                .get()
                .toPromise();

            if (policyDoc?.exists) {
                this.policy = policyDoc.data() as OrgPolicy;
            }
        } catch (error) {
            console.error('Error loading policy:', error);
            this.snackBar.open('Error loading policy content', 'Close', { duration: 3000 });
        } finally {
            this.isLoadingPolicy = false;
        }
    }

    public async onStepComplete(step: 'read' | 'understood' | 'agreed'): Promise<void> {
        // If this is a new acknowledgment and no document exists, create it first
        if (this.isNewAcknowledgment && !this.acknowledgmentKey) {
            await this.createNewAcknowledgment();
        }

        // Don't save if we don't have a key yet
        if (!this.acknowledgmentKey) return;

        // Save progress as user completes each step
        try {
            const now = firebase.firestore.Timestamp.now();
            const updateData: { [key: string]: firebase.firestore.Timestamp | boolean | null } = {
                updatedAt: now,
            };

            switch (step) {
                case 'read':
                    updateData['hasRead'] = this.hasRead;
                    if (this.hasRead) updateData['readAt'] = now;
                    break;
                case 'understood':
                    updateData['hasUnderstood'] = this.hasUnderstood;
                    if (this.hasUnderstood) updateData['understoodAt'] = now;
                    break;
                case 'agreed':
                    updateData['hasAgreed'] = this.hasAgreed;
                    if (this.hasAgreed) updateData['agreedAt'] = now;
                    break;
            }

            await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policyAcknowledgments')
                .doc(this.acknowledgmentKey)
                .update(updateData);
        } catch (error) {
            console.error('Error saving acknowledgment progress:', error);
        }
    }

    /**
     * Create a new acknowledgment document when user starts the acknowledgment process.
     * Uses composite key (policyKey_uid) to prevent duplicates.
     */
    private async createNewAcknowledgment(): Promise<void> {
        try {
            const now = firebase.firestore.Timestamp.now();

            // Use composite key to prevent duplicates: policyKey_uid
            const compositeKey = `${this.data.acknowledgment.policyKey}_${this.data.acknowledgment.uid}`;

            const newDocRef = this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policyAcknowledgments')
                .doc(compositeKey);

            // Check if acknowledgment already exists (created by backend)
            const existingDoc = await newDocRef.ref.get();
            if (existingDoc.exists) {
                // Already exists - just update our reference
                this.acknowledgmentKey = compositeKey;
                this.isNewAcknowledgment = false;
                this.data.acknowledgment.key = this.acknowledgmentKey;
                return;
            }

            this.acknowledgmentKey = compositeKey;

            const newAcknowledgment = {
                ...this.data.acknowledgment,
                key: this.acknowledgmentKey,
                createdAt: now,
                updatedAt: now,
                hasRead: false,
                hasUnderstood: false,
                hasAgreed: false,
                isComplete: false,
            };

            await newDocRef.set(newAcknowledgment);
            this.isNewAcknowledgment = false; // Now it exists

            // Update the data object so the dialog result is correct
            this.data.acknowledgment.key = this.acknowledgmentKey;
        } catch (error) {
            console.error('Error creating acknowledgment:', error);
            this.snackBar.open('Error starting acknowledgment. Please try again.', 'Close', { duration: 3000 });
        }
    }

    public async completeAcknowledgment(): Promise<void> {
        if (!this.hasRead || !this.hasUnderstood || !this.hasAgreed) {
            this.snackBar.open('Please complete all acknowledgment steps', 'Close', { duration: 3000 });
            return;
        }

        // If this is a new acknowledgment and no document exists, create it first
        if (this.isNewAcknowledgment && !this.acknowledgmentKey) {
            await this.createNewAcknowledgment();
        }

        if (!this.acknowledgmentKey) {
            this.snackBar.open('Error: Could not create acknowledgment record', 'Close', { duration: 3000 });
            return;
        }

        this.isSaving = true;

        try {
            const now = firebase.firestore.Timestamp.now();
            const updateData: { [key: string]: firebase.firestore.Timestamp | boolean | null } = {
                hasRead: true,
                hasUnderstood: true,
                hasAgreed: true,
                isComplete: true,
                completedAt: now,
                updatedAt: now,
                // Clear re-acknowledgment flag if it was set
                requiresReacknowledgment: false,
                reacknowledgmentRequiredAt: null,
            };

            await this.db
                .collection('organizations')
                .doc(this.data.organization.key)
                .collection('policyAcknowledgments')
                .doc(this.acknowledgmentKey)
                .update(updateData);

            this.snackBar.open('Policy acknowledged successfully!', 'Close', { duration: 3000 });

            // Return success result
            const result: PolicyAcknowledgmentDialogResult = {
                completed: true,
                acknowledgment: {
                    ...this.data.acknowledgment,
                    hasRead: true,
                    hasUnderstood: true,
                    hasAgreed: true,
                    isComplete: true,
                    completedAt: now as unknown as import('firebase/firestore').Timestamp,
                },
            };

            this.dialogRef.close(result);
        } catch (error) {
            console.error('Error completing acknowledgment:', error);
            this.snackBar.open('Error saving acknowledgment. Please try again.', 'Close', { duration: 3000 });
        } finally {
            this.isSaving = false;
        }
    }
}
